import { ClockCircleOutlined } from "@ant-design/icons";
import { createFileRoute } from "@tanstack/react-router";
import { useConfirmRegistrationQuery, useConfirmRegistrationResendMutation } from "api/User/hooks.ts";
import { AuthAlert } from "components/UI/AuthAlert/AuthAlert.tsx";
import { AuthPageContainer } from "components/UI/AuthPageContainer/AuthPageContainer.tsx";
import { RegistrationSuccess } from "components/UI/RegistrationSuccess/RegistrationSuccess.tsx";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { useState } from "react";
import { toast } from "react-toastify";
import { RoleEnum } from "types/enums.ts";

import ConfirmRegistrationResendRequest = App.Http.Requests.User.ConfirmRegistrationResendRequest;

export const Route = createFileRoute("/$tenantSlug/auth/_layout/confirm-registration/")({
    component: ConfirmRegistrationPage,
});

function ConfirmRegistrationPage() {
    const { tenantSlug } = Route.useParams();
    const { t: tAuth } = usePaldockTranslation("auth.confirm-registration");
    const { t: tRegistration } = usePaldockTranslation("auth.registration");
    const [linkResent, setLinkResent] = useState(false);
    const url = new URL(window.location.href);
    const userId = url.searchParams.get("user_id") ?? "";
    const tenantId = url.searchParams.get("tenant_id") ?? "";
    // Forwarded verbatim from the URL — never fabricate a role. tenant_id/role are part of the
    // signed link's signature, so a missing/wrong value here naturally fails as an invalid link.
    const role = (url.searchParams.get("role") ?? "") as RoleEnum;
    const expires = url.searchParams.get("expires") ?? "";
    const signature = url.searchParams.get("signature") ?? "";

    const confirmRegistrationQuery = useConfirmRegistrationQuery({
        user_id: userId,
        tenant_id: tenantId,
        role,
        expires,
        signature,
    });

    const resendRequest: ConfirmRegistrationResendRequest = {
        user_id: userId,
        tenant_id: tenantId,
        role,
        return_url: `${import.meta.env.VITE_APP_URL}/${tenantSlug}/auth/confirm-registration`,
    };
    const resendMutation = useConfirmRegistrationResendMutation({
        onSuccess: () => {
            toast.success(tAuth("messages.link_resent"));
            setLinkResent(true);
        },
    });

    return (
        <AuthPageContainer title={tRegistration("title")}>
            {confirmRegistrationQuery.isLoading ? null : confirmRegistrationQuery.isSuccess ? (
                <RegistrationSuccess
                    message={tAuth("messages.success_message")}
                    icon={<ClockCircleOutlined style={{ fontSize: "167px", color: "#FFA940" }} />}
                    alertType="warning"
                />
            ) : linkResent ? (
                <AuthAlert type="success" message={tAuth("messages.link_resent")} />
            ) : (
                <AuthAlert
                    type="error"
                    message={tAuth("messages.invalid_link")}
                    description={
                        <a
                            onClick={() => resendMutation.mutate(resendRequest)}
                            style={{ color: "#171717", textDecoration: "underline" }}
                            className="hover:!no-underline hover:!text-[#171717]"
                        >
                            {tAuth("actions.resend_link")}
                        </a>
                    }
                />
            )}
        </AuthPageContainer>
    );
}
